import React from 'react';
import styled from 'styled-components';
import { Volume2, Loader2, CheckCircle } from 'lucide-react';

const Container = styled.div`
  margin: 24px 0;
  padding: 12px 0;
  border-bottom: 1px solid #111010ff;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const SentenceTitle = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 6px;
`;

const Label = styled.div`
  color: #e50914;
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 4px;
  margin-top: 12px;
`;

const WordTag = styled.span`
  background: #333; /* Brighter than #222 */
  color: #f0f0f0;
  border: 1px solid #444; /* Definition border */
  border-radius: 7px;
  padding: 6px 12px;
  margin: 4px 6px 4px 0;
  display: inline-block;
  font-size: 1rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #444;
    border-color: #666;
    transform: translateY(-1px);
  }
`;

const MatchTag = styled(WordTag)`
  background: #fffbe6;
  color: #222;
  box-shadow: 0 1px 4px rgba(229,9,20,0.06);

  &:hover {
    background: #fff;
  }
`;

const CompleteText = styled.div`
  margin-top: 14px;
  color: #e5e5e5;
  font-size: 0.98rem;
  line-height: 1.5;
  word-break: keep-all;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #b3b3b3;
  padding: 4px;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #fff;
  }
`;

function AnalyzedResult({ item, idx, speak, onWordClick, onToggleComplete, autoScroll }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [activeUnitIndex, setActiveUnitIndex] = React.useState(-1);
  const elementRef = React.useRef(null);

  React.useEffect(() => {
    if (autoScroll && elementRef.current) {
      // Small timeout to ensure rendering layout is stable
      setTimeout(() => {
        elementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [autoScroll]);

  // Pre-calculate unit char lengths for estimation
  // We assume the TTS text roughly matches the joined units
  const units = item.meaningUnits || [];
  const unitLengths = React.useMemo(() => units.map(u => u.source.length), [units]);
  const totalLength = React.useMemo(() => unitLengths.reduce((a, b) => a + b, 0), [unitLengths]);

  const handleSpeak = async () => {
    if (isLoading || isPlaying) return;
    setIsLoading(true);
    setIsPlaying(true);
    setActiveUnitIndex(-1);

    // Use units text to ensure better alignment for estimation, 
    // unless original is significantly different (fallback)
    const ttsText = units.map(u => u.source).join(' ') || item.original;

    try {
      await speak({
        text: ttsText,
        rate: 0.9,
        pitch: 1,
        onProgress: (currentTime, duration) => {
          if (!duration || totalLength === 0) return;
          const progress = currentTime / duration;
          const currentChar = progress * totalLength;

          let accumulated = 0;
          let foundIndex = -1;
          for (let i = 0; i < unitLengths.length; i++) {
            accumulated += unitLengths[i];
            // Add estimated space/gap? simplified: just length
            // Since we joined by ' ', maybe add +1? 
            // Let's keep it simple: ratio map on pure content length
            if (currentChar <= accumulated) {
              foundIndex = i;
              break;
            }
          }
          if (foundIndex !== -1) setActiveUnitIndex(foundIndex);
        },
        onEnd: () => {
          setIsPlaying(false);
          setActiveUnitIndex(-1);
        }
      });
    } catch (error) {
      console.error(error);
      setIsPlaying(false);
      setActiveUnitIndex(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const isCompleted = item.completed || false;

  // Container highlight removed (User requested unit highlight)
  const containerStyle = {
    opacity: isCompleted ? 0.6 : 1,
    transition: 'all 0.3s',
    borderRadius: '8px',
    padding: '12px',
    borderBottom: '1px solid #111010ff',
  };

  return (
    <Container ref={elementRef} style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SentenceTitle>문장 {idx + 1}</SentenceTitle>
        <IconButton
          onClick={() => onToggleComplete(idx)}
          title={isCompleted ? "완료 취소" : "완료 표시"}
          style={{ color: isCompleted ? '#4caf50' : '#444' }}
        >
          <CheckCircle size={20} fill={isCompleted ? "#4caf50" : "none"} />
        </IconButton>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', marginTop: '4px' }}>
        <Label style={{ margin: 0 }}>원문:</Label>
        <IconButton
          onClick={handleSpeak}
          title="전체 듣기"
          disabled={isLoading}
        >
          {isLoading ? <Loader2 size={16} className="animate-spin" /> :
            isPlaying ? <Volume2 size={16} color="#e50914" className="animate-pulse" /> :
              <Volume2 size={16} />}
        </IconButton>
      </div>
      <div style={{ marginBottom: 12, flexWrap: 'wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
        {item.meaningUnits && item.meaningUnits.length > 0 ? (
          item.meaningUnits.map((unit, i) => (
            <WordTag
              key={i}
              onClick={() => onWordClick(unit.source, unit.target)}
              title={unit.source}
              style={{
                background: i === activeUnitIndex ? '#e50914' : undefined,
                color: i === activeUnitIndex ? '#fff' : undefined,
                transform: i === activeUnitIndex ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.2s'
              }}
            >
              {unit.source}
            </WordTag>
          ))
        ) : (
          <span style={{ color: '#aaa' }}>매칭 결과 없음</span>
        )}
      </div>
      <Label>매칭 번역:</Label>
      <div style={{ marginBottom: 12, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
        {item.meaningUnits && item.meaningUnits.length > 0 ? (
          item.meaningUnits.map((unit, i) => (
            <MatchTag
              key={i}
              onClick={() => onWordClick(unit.source, unit.target)}
              title={unit.target}
              style={{
                background: i === activeUnitIndex ? '#ffd1d1' : undefined, // Light red for active match
                borderColor: i === activeUnitIndex ? '#e50914' : undefined,
                transition: 'background 0.2s'
              }}
            >
              {unit.target}
            </MatchTag>
          ))
        ) : (
          <span style={{ color: '#aaa' }}>매칭 결과 없음</span>
        )}
      </div>
      <CompleteText>
        <strong>완성 번역:</strong> {item.completedTranslation}
      </CompleteText>
    </Container>
  );
}

export default AnalyzedResult;
